import type { LayerHandle } from "../input/router.ts";
import type { KitInput } from "./input.tsx";

/**
 * A modal's layer in the input router (CLIENT.md 7.3 and 7.7), kept for as long as the modal is open. The
 * router takes a layer out of its stack before it asks the layer's owner to close, and the owner's page may
 * keep the modal open (a form that is still sending, a discard that needs a second request); a modal that
 * lost its layer that way would still be on screen with the next Escape reaching whatever lies beneath it,
 * the game menu included.
 */

/**
 * Opens a layer that stays on top of the router's stack while its owner stays open. Each time the router
 * closes the layer (Escape, a close key, `dismiss()`), the owner is asked to close; unless it let go of the
 * layer while it was asked, a fresh layer takes the old one's place at once, so the next Escape asks it
 * again.
 *
 * @param input The router's layer stack.
 * @param onClose Asks the owner to close, as Escape does.
 * @returns A function that takes the layer away for good without asking the owner, for when the modal
 *   leaves the page; calling it again does nothing.
 * @example
 * const release = keepLayer(router, () => setOpen(false));
 * release();
 */
export function keepLayer(input: Pick<KitInput, "open">, onClose: () => void): () => void {
  let released = false;
  const current: { layer: LayerHandle | null } = { layer: null };

  /**
   * Puts the owner's layer on top of the stack.
   */
  const open = (): void => {
    current.layer = input.open({
      /**
       * Asks the owner to close, and opens the layer again if the owner stays.
       */
      onClose: () => {
        if (released) {
          return;
        }
        onClose();
        if (!released) {
          open();
        }
      },
    });
  };
  open();
  return () => {
    if (released) {
      return;
    }
    released = true;
    current.layer?.close();
  };
}
